// In src/pages/NotificationsPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
  collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc
} from 'firebase/firestore';
import {
  Container, Typography, List, ListItem, ListItemText,
  CircularProgress, Paper, Divider, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatDistanceToNow } from 'date-fns';

function NotificationsPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set up a real-time listener for this user's notifications
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    const notificationsRef = collection(db, 'users', currentUser.uid, 'userNotifications');
    const q = query(notificationsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedNotifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotifications(fetchedNotifications);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching notifications:", error);
      setLoading(false);
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [currentUser]);

  // Handle clicking a notification
  const handleNotificationClick = async (notification) => {
    // Mark the notification as read in Firestore
    if (!notification.isRead) {
      const notificationRef = doc(db, 'users', currentUser.uid, 'userNotifications', notification.id);
      await updateDoc(notificationRef, { isRead: true });
    }
    
    // Check if the link is a full URL
    const isExternalUrl = notification.link && notification.link.startsWith('http');
    const isAttachmentUrl = notification.attachmentUrl && notification.attachmentUrl.startsWith('http');

    if (isAttachmentUrl) {
        // Prioritize the dedicated attachmentUrl field
        window.open(notification.attachmentUrl, '_blank');
    } else if (isExternalUrl) {
        // If an attachmentUrl is not present, check if the main link is an external URL
        window.open(notification.link, '_blank');
    } else if (notification.link && notification.link !== '/') {
        // For internal links, use React Router to navigate
        navigate(notification.link);
    }
  };

  // Handle deleting a notification
  const handleDeleteNotification = async (notificationId, event) => {
    // Stop event propagation to prevent handleNotificationClick from also firing
    event.stopPropagation(); 
    if (!currentUser?.uid) return;

    if (window.confirm("Are you sure you want to delete this notification?")) {
      const notificationRef = doc(db, 'users', currentUser.uid, 'userNotifications', notificationId);
      try {
        await deleteDoc(notificationRef);
        // UI will automatically update due to onSnapshot listener
      } catch (error) {
        console.error("Error deleting notification:", error);
        alert("Failed to delete notification.");
      }
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>Notifications</Typography>
      <Paper>
        <List sx={{ padding: 0 }}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText primary="You have no notifications." />
            </ListItem>
          ) : (
            notifications.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  alignItems="flex-start"
                  onClick={() => handleNotificationClick(item)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: item.isRead ? 'transparent' : 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={(e) => handleDeleteNotification(item.id, e)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: item.isRead ? 'normal' : 'bold' }}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          sx={{ display: 'block' }}
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {item.body}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.createdAt ?
                            formatDistanceToNow(item.createdAt.toDate(), { addSuffix: true }) :
                            'Just now'}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>
    </Container>
  );
}

export default NotificationsPage;